import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input, Select, EmptyState } from "../components/UI";
import { Plus, Image as ImageIcon, Trash2, CheckCircle2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FORMATS = ["display", "video", "native", "audio", "ctv", "radio", "dooh"];

export default function Creatives() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", format: "display", dimensions: "300x250", destination_url: "",
    utm: "", asset_url: "", client_id: "", tags: "",
  });
  const [clients, setClients] = useState([]);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/creatives"), api.get("/clients")]);
    setItems(a.data); setClients(b.data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    await api.post("/creatives", {
      ...form,
      tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
      client_id: form.client_id || null,
    });
    setOpen(false); load();
  };

  const handleApprove = async (id) => { await api.put(`/creatives/${id}/approve`); load(); };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this creative?")) return;
    await api.delete(`/creatives/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Creative Library"
        title="Creatives"
        subtitle="Display, video, native, audio, CTV, and radio assets. Generate AI banners on demand."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => navigate("/creative-studio")} data-testid="open-studio-btn">
              <Sparkles className="w-4 h-4" /> AI Creative Studio
            </Btn>
            <Btn onClick={() => setOpen(true)} data-testid="new-creative-btn">
              <Plus className="w-4 h-4" /> Upload
            </Btn>
          </div>
        }
      />

      {items.length === 0 ? (
        <Card>
          <EmptyState icon={ImageIcon} title="No creatives yet" description="Upload assets or generate AI banners." action={<Btn onClick={() => navigate("/creative-studio")}><Sparkles className="w-4 h-4" /> Open AI Studio</Btn>} />
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="creatives-grid">
          {items.map(cr => (
            <Card key={cr.id} padded={false} className="overflow-hidden">
              <div className="aspect-video bg-slate-900/60 flex items-center justify-center overflow-hidden">
                {(cr.asset_base64 || cr.asset_url) ? (
                  <img src={cr.asset_base64 || cr.asset_url} alt={cr.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-slate-600" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-white truncate">{cr.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{cr.format} · {cr.dimensions || "—"}</div>
                  </div>
                  <Pill status={cr.approval_status} />
                </div>
                {cr.is_ai_generated && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-cyan-300 uppercase tracking-wider">
                    <Sparkles className="w-3 h-3" /> AI generated
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  {cr.approval_status !== "Approved" && (
                    <button onClick={() => handleApprove(cr.id)} className="flex-1 text-xs px-2 py-1 rounded-md bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20 transition-colors" data-testid={`approve-${cr.id}`}>
                      <CheckCircle2 className="w-3 h-3 inline" /> Approve
                    </button>
                  )}
                  <button onClick={() => handleDelete(cr.id)} className="text-xs px-2 py-1 rounded-md text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-app border border-white/10 rounded-lg max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">Upload creative</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" data-testid="creative-name-input" />
              <Select label="Format" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
                {FORMATS.map(f => <option key={f}>{f}</option>)}
              </Select>
              <Input label="Dimensions" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} placeholder="300x250" />
              <Input label="Asset URL" value={form.asset_url} onChange={(e) => setForm({ ...form, asset_url: e.target.value })} className="col-span-2" placeholder="https://…" />
              <Input label="Destination URL" value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} className="col-span-2" />
              <Input label="UTM" value={form.utm} onChange={(e) => setForm({ ...form, utm: e.target.value })} />
              <Select label="Client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="">Unassigned</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.business_name}</option>)}
              </Select>
              <Input label="Tags (comma)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="col-span-2" />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={handleCreate} data-testid="save-creative-btn">Save creative</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
